import { useMemo } from 'react';

import type { PartnerStripeDocumentData } from '@snowit/models';
import type { PartnerModel, PartnerStripeModel } from '@snowit/types/partners';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import useSchemas from '../useSchemas';
import useTenants from '../useTenants';

export default () => {
  const firebase = useFirebase();
  const { writeDocument, deleteDocument } = useSchemas();
  const { store, setStore } = useStore();
  const { activeTenants } = useTenants();

  const partners = useMemo((): PartnerModel[] => {
    return store.partners.list
      .filter((partner) => {
        if (!activeTenants.length) return true;
        return activeTenants.some((it) => (partner.tenants ?? []).includes(it));
      })
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [store.partners.list, activeTenants]);

  const active = useMemo((): PartnerModel | undefined => {
    if (!partners.length || !store.partners.selected) return undefined;
    return partners.find((entry) => entry.uid === store.partners.selected);
  }, [partners, store.partners.selected]);

  const getPartners = async (): Promise<PartnerModel[]> => {
    try {
      const res = await firebase.query<PartnerModel>({
        collectionName: 'partners',
        filters: (q) => q.limit(0),
      });
      setStore('partners', { list: res });
      return res;
    } catch (error) {
      console.error('partners_get', { error });
      return [];
    } finally {
      setStore('partners', { ready: true });
    }
  };

  const getPartnerStripe = async (id: string): Promise<PartnerStripeModel | undefined> => {
    try {
      const doc = await firebase.doc<PartnerStripeModel>({
        collectionName: 'partnerStripe',
        id,
      });
      return doc;
    } catch (error) {
      console.error('partners_get', { error });
      return undefined;
    }
  };

  const selectPartner = (uid?: string) => {
    if (!uid) {
      window.localStorage.removeItem('partner');
    } else {
      window.localStorage.setItem('partner', uid);
    }
    setStore('partners', { selected: uid });
  };

  const writePartner = async (
    partnerUID: string | undefined,
    data: Partial<PartnerModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.PARTNER,
        attributes: omitter(data),
        id: isNewEntity(partnerUID) ? undefined : partnerUID,
      });
      await getPartners();
      return id;
    } catch (error: any) {
      console.error('writePartner', { error });
      return undefined;
    }
  };

  const writePartnerStripe = async (
    partnerStripeUID: string,
    data: Partial<PartnerStripeModel>,
  ): Promise<string | undefined> => {
    if (isNewEntity(partnerStripeUID)) return undefined;
    try {
      const id = await writeDocument({
        modelName: SchemaNames.PARTNERSTRIPE,
        attributes: omitter(data),
        id: partnerStripeUID,
      });
      return id;
    } catch (error: any) {
      console.error('writePartnerStripe', { error });
      return undefined;
    }
  };

  const deletePartner = async (partnerUID: string): Promise<boolean> => {
    if (isNewEntity(partnerUID)) return false;
    try {
      await deleteDocument({
        modelName: SchemaNames.PARTNER,
        id: partnerUID,
      });
      await getPartners();
      return true;
    } catch (error: any) {
      console.error('writePartner', { error });
      return false;
    }
  };

  const deletePartnerStripe = async (partnerStripeUID: string): Promise<boolean> => {
    if (isNewEntity(partnerStripeUID)) return false;
    try {
      await deleteDocument({
        modelName: SchemaNames.PARTNERSTRIPE,
        id: partnerStripeUID,
      });
      return true;
    } catch (error: any) {
      console.error('writePartnerStripe', { error });
      return false;
    }
  };

  const acceptTos = async (partnerUID: string, tenant: string, accept = true) => {
    try {
      await firebase.call('partners-tosAcceptance', { partnerUID, tenant, accept });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const updatePartnerStripeDocument = async (
    partnerUID: string,
    tenant: string,
    payload: Omit<Partial<PartnerStripeDocumentData>, 'tosAcceptance'>,
  ) => {
    try {
      await firebase.call('partnerStripe-updatePartnerStripeDocument', {
        partnerUID,
        tenant,
        payload,
      });
    } catch (error) {
      console.error('[updatePartnerStripeDocument]', error);
    }
  };

  return {
    ready: store.partners.ready,
    partners,
    partner: store.partners.selected,
    active,
    getPartners,
    getPartnerStripe,
    selectPartner,
    writePartner,
    writePartnerStripe,
    acceptTos,
    deletePartner,
    deletePartnerStripe,
    updatePartnerStripeDocument,
  };
};
